import React, { useMemo } from 'react';
import { DataTable, Code } from '@scale/scaleui-radix';
import { createColumnHelper } from '@tanstack/react-table';

const EmployeeInformationTable = () => {
  const employeeMetadata = useMemo(
    () => ({
      employeeName: {
        description: 'The name of the employee for whom the order is being placed',
        required: true,
      },
      email: {
        description: 'The email address of the employee (for order notifications)',
        required: false,
      },
      mobile: {
        description: 'The mobile phone number of the employee (for order updates)',
        required: false,
      },
      orderNote: {
        description: 'Special instructions or notes for the order',
        required: false,
      },
    }),
    [],
  );

  // Generate table data from employee metadata
  const data = useMemo(
    () =>
      Object.entries(employeeMetadata).map(([columnName, { description, required }]) => ({
        columnName,
        description: required ? `(Required) - ${description}` : `(Optional) - ${description}`,
      })),
    [employeeMetadata],
  );

  // Create column helper
  const columnHelper = createColumnHelper<{ columnName: string; description: string }>();

  // Define columns and cell components
  const ColumnNameCell = ({ value }: { value: string }) => <Code>{value}</Code>;
  ColumnNameCell.displayName = 'ColumnNameCell';

  const DescriptionCell = ({ value }: { value: string }) => <>{value}</>;
  DescriptionCell.displayName = 'DescriptionCell';

  const renderColumnName = (info: any) => <ColumnNameCell value={info.getValue()} />;
  renderColumnName.displayName = 'renderColumnName';

  const renderDescription = (info: any) => <DescriptionCell value={info.getValue()} />;
  renderDescription.displayName = 'renderDescription';

  const columns = [
    columnHelper.accessor('columnName', {
      id: 'columnName',
      header: 'Column Name',
      cell: renderColumnName,
    }),
    columnHelper.accessor('description', {
      id: 'description',
      header: 'Description',
      cell: renderDescription,
    }),
  ];

  return (
    <DataTable.Root
      columns={columns}
      data={data}
      enableSorting={false}
      enableColumnFilters={false}
      initialState={{
        pagination: {
          pageIndex: 0,
          pageSize: 10,
        },
      }}
    >
      {({ table }) => (
        <DataTable.Table>
          <DataTable.Header>
            {table.getHeaderGroups().map(headerGroup => (
              <DataTable.HeaderGroup key={headerGroup.id}>
                {headerGroup.headers.map(header => (
                  <DataTable.ColumnHeaderCell key={header.id} header={header} />
                ))}
              </DataTable.HeaderGroup>
            ))}
          </DataTable.Header>
          <DataTable.Body>
            {table.getRowModel().rows.map(row => (
              <DataTable.Row key={row.id} row={row}>
                {row.getVisibleCells().map(cell => (
                  <DataTable.Cell key={cell.id} cell={cell} />
                ))}
              </DataTable.Row>
            ))}
          </DataTable.Body>
        </DataTable.Table>
      )}
    </DataTable.Root>
  );
};

EmployeeInformationTable.displayName = 'EmployeeInformationTable';

export default EmployeeInformationTable;
